import { useState } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useWarehouses } from './hooks/useWarehouses';
import { WarehousesTable } from './components/WarehousesTable';
import { WarehousesToolbar } from './components/WarehousesToolbar';
import { WarehouseModal } from './components/WarehouseModal';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { Warehouse } from './types/warehouses.types';

export function WarehousesPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.warehouses.view');

  const {
    data,
    total,
    isLoading,
    filters,
    updateSearch,
    updateStatus,
    updatePage,
    deleteWarehouse,
    isDeleting,
  } = useWarehouses();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Warehouse | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Warehouse | null>(null);

  const handleEdit = (item: Warehouse) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteWarehouse(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <PageLayout title="Kho bãi" description="Quản lý danh sách kho bãi và địa điểm lưu trữ">
      <div className="flex flex-col h-full gap-4">
        <WarehousesToolbar
          filters={filters}
          onSearchChange={updateSearch}
          onStatusChange={updateStatus}
          onAdd={handleAdd}
        />

        <div className="flex-1 bg-white border border-slate-200 rounded-md overflow-hidden min-h-0">
          <WarehousesTable
            items={data}
            total={total}
            page={filters.page}
            pageSize={filters.pageSize}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
            onPageChange={updatePage}
          />
        </div>
      </div>

      <WarehouseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        warehouse={editingItem}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa"
        message={
          <span>
            Bạn có chắc chắn muốn xóa kho bãi <strong>{deleteTarget?.name}</strong>?
            Hành động này không thể hoàn tác.
          </span>
        }
        confirmText="Xóa kho bãi"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}
